import { useState, useEffect, useCallback, useRef } from 'react'
import {
  doc,
  getDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore'
import { db } from '../firebase/firebaseConfig'
import { useAuth } from '../components/auth/AuthProvider'

export function useFavorites() {
  const { isAuthenticated, user } = useAuth()
  const [favorites, setFavorites] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Ref to always have current favorites in callbacks without stale closures
  const favoritesRef = useRef<Set<string>>(favorites)
  favoritesRef.current = favorites

  // Load favorites from Firestore
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setFavorites(new Set())
      return
    }

    const loadFavorites = async () => {
      setLoading(true)
      setError(null)
      try {
        const favRef = doc(db, 'users', user.id, 'data', 'favorites')
        const favDoc = await getDoc(favRef)

        if (favDoc.exists()) {
          const data = favDoc.data()
          const ids = data.productIds || []
          setFavorites(new Set(ids))
        } else {
          setFavorites(new Set())
        }
      } catch (err: any) {
        const errorCode = err?.code || 'unknown'
        const errorMessage = err?.message || String(err)
        console.error('[Favorites] Load failed:', err)
        setError(`Load failed: ${errorCode} — ${errorMessage}`)
      } finally {
        setLoading(false)
      }
    }

    loadFavorites()
  }, [isAuthenticated, user?.id])

  // Toggle favorite with atomic Firestore update
  const toggleFavorite = useCallback(
    async (productId: string) => {
      if (!isAuthenticated || !user?.id) {
        return
      }

      const favRef = doc(db, 'users', user.id, 'data', 'favorites')
      // Use ref to get current state (avoids stale closure)
      const isCurrentlyFavorited = favoritesRef.current.has(productId)

      // Optimistic UI update
      setFavorites((prev) => {
        const next = new Set(prev)
        if (isCurrentlyFavorited) {
          next.delete(productId)
        } else {
          next.add(productId)
        }
        return next
      })
      setError(null)

      // Persist to Firestore atomically
      try {
        if (isCurrentlyFavorited) {
          await setDoc(
            favRef,
            { productIds: arrayRemove(productId) },
            { merge: true },
          )
        } else {
          await setDoc(
            favRef,
            { productIds: arrayUnion(productId) },
            { merge: true },
          )
        }
      } catch (err: any) {
        const errorCode = err?.code || 'unknown'
        const errorMessage = err?.message || String(err)
        console.error('[Favorites] Write failed:', err)

        setError(`Write failed: ${errorCode} — ${errorMessage}`)

        // Revert on error
        setFavorites((prev) => {
          const reverted = new Set(prev)
          if (isCurrentlyFavorited) {
            reverted.add(productId)
          } else {
            reverted.delete(productId)
          }
          return reverted
        })
      }
    },
    [isAuthenticated, user?.id],
  )

  const isFavorited = useCallback(
    (productId: string) => favorites.has(productId),
    [favorites],
  )

  return {
    favorites,
    toggleFavorite,
    isFavorited,
    favoritesCount: favorites.size,
    loading,
    error,
  }
}
